import { ErrorHelper } from '@eway-crm/connector';
import type { HostingApi } from '@eway-crm/hostingdaemon-js-lib';
import LocalizationHelper from '../localization/LocalizationHelper';
import EmailDomainsProviders from '@eway-crm/js-resources/assets/Json/EmailDomainsProviders.json';

export class StringHelper {
    /**
     * Prepares the string for sending to eway by e.g. trimming it
     */
    static prepareForEway = (string: string) => {
        return string.trim();
    };

    static loadNameDeclension = async (hostingApi: HostingApi, firstName: string) => {
        if (LocalizationHelper.isLanguageCs()) {
            const res = await hostingApi.getNameDeclension({ firstName });
            if (res && !res.IsErrorResponse && res.Value) {
                return res.Value;
            }
            return null;
        }

        return firstName;
    };

    /**
     * Naive approach to get first and last name from email. In many cases it will be incorrect.
     */
    static getFirstAndLastNameFromEmail = (email: string) => {
        const firstPart = email.substring(0, email.lastIndexOf("@"));

        if (firstPart.includes('.')) {
            const [firstName, ...lastNameParts] = firstPart.split('.');
            const lastName = lastNameParts.join(".");
            return {
                firstName: StringHelper.capitalize(firstName),
                lastName: StringHelper.capitalize(lastName),
            };
        }

        return {
            firstName: firstPart,
            lastName: '',
        };
    };

    static readonly extractDomainFromEmail = (email: string) => {
        // Regular expression to match the domain part of an email address
        const domainRegex = /@([^@\s]+\.[^@\s]+)/;
        const match = email.match(domainRegex);
        return match ? match[1] : "";
    };

    /**
     * Checks whether the email uses a generic (popular/freemail) domain like gmail.com, outlook.com, etc.
     * @param email
     * @returns true for generic domains (or when no domain can be extracted), false for company domains
     */
    static readonly isGenericEmailDomain = (email: string) => {
        const domain = StringHelper.extractDomainFromEmail(email).toLowerCase();
        if (!domain) {
            return true;
        }

        // Match the exact domain or any subdomain of a listed provider, so tenant defaults
        // like whklt.onmicrosoft.com are recognized as generic (the list holds the bare onmicrosoft.com).
        return EmailDomainsProviders.Domains.some(d => {
            const listed = d.toLowerCase();
            return domain === listed || domain.endsWith(`.${listed}`);
        });
    };

    /**
     * Extracts website from email if it is not blocked (popular) domain
     * @param email
     * @returns
     */
    static readonly getNonBlockedWebsiteFromEmail = (email: string) => {
        if (StringHelper.isGenericEmailDomain(email)) {
            return "";
        }

        return `www.${StringHelper.extractDomainFromEmail(email)}`;
    };

    static capitalize = (string: string) => {
        if (!string) {
            return '';
        }

        return string[0].toUpperCase() + string.slice(1);
    };

    static prepareErrorLog = (e: Error, info: string) => {
        return `${info}. \n${ErrorHelper.stringifyError(e)}`;
    };

    /**
     * Removes characters from start of the string, including any whitespace
     */
    static removeCharactersFromStart(inputString: string, charactersToRemove: string): string {
        let resultString = inputString;

        for (const char of charactersToRemove) {
            const escapedChar = char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`^${escapedChar}\\s?`);
            resultString = resultString.replace(regex, '');
        }

        return resultString;
    }
}
